import {
	CURP_EXTRACTION_TERMINAL_STATUSES,
	type CurpExtractionState,
} from '../../services/curpExtraction.service';

/**
 * Contrato: tras disparar una validación nueva de un CURP que ya tenía
 * evidencia, un estado terminal cuya evidencia no es más nueva que la que
 * existía al disparar NO es el resultado de esa validación. Se reporta como
 * `running`, sin los datos viejos, para que el hub diga "validando" y el
 * polling siga. El GET /curp-extraction está llaveado por CURP y devuelve la
 * fila más reciente de cualquier aliado y fecha (t-279, 13-oct, usuario
 * 608542: el hub decía "Listo" con una fila de 20 minutos antes).
 *
 * La comparación es entre timestamps del mismo servidor (updated_at de la
 * fila), así que no depende del reloj del dispositivo. Pasado
 * NEWER_EVIDENCE_WAIT_CAP_MS sin evidencia nueva (el workflow murió sin dejar
 * fila) se vuelve a mostrar lo que haya.
 */
export const NEWER_EVIDENCE_WAIT_CAP_MS = 15 * 60_000;

export type NewerEvidenceRequirement = {
	curp: string;
	baselineEvidenceAt: string;
	requiredSince: number;
};

export const evidenceAt = (state: CurpExtractionState): string | null =>
	state.sources?.validation?.updated_at ?? state.extracted_at ?? null;

/** Ventana en la que un `stale` todavía es un reintento en curso: desde el
 * 20-oct b2b-workflow-manager reintenta el nombre a los 5, 10 y 20 min sin
 * escribir la fila (fiscal-engine marca `stale` tras 5 min sin escritura). */
export const STALE_RETRY_WINDOW_MS = 40 * 60_000;

/**
 * Contrato: `stale` con la última escritura de la fila dentro de
 * STALE_RETRY_WINDOW_MS se trata como validación en curso (se sigue polleando,
 * el hub dice "validando" y no se reporta como fallo). Pasada la ventana es un
 * `stale` real: la corrida murió y el usuario puede recapturar. Una sola regla
 * para el polling, el hub y el tracking.
 */
export const isStaleRetrying = (state: CurpExtractionState | null | undefined, now: number): boolean => {
	if (state?.status !== 'stale') return false;
	const lastWrite = Date.parse(evidenceAt(state) ?? '');
	return !Number.isNaN(lastWrite) && now - lastWrite < STALE_RETRY_WINDOW_MS;
};

export const applyNewerEvidence = (
	state: CurpExtractionState | null,
	requirement: NewerEvidenceRequirement | null,
	now: number,
): CurpExtractionState | null => {
	if (!state || !requirement || state.curp !== requirement.curp) return state;
	if (!CURP_EXTRACTION_TERMINAL_STATUSES.includes(state.status)) return state;
	if (now - requirement.requiredSince > NEWER_EVIDENCE_WAIT_CAP_MS) return state;
	const current = Date.parse(evidenceAt(state) ?? '');
	const baseline = Date.parse(requirement.baselineEvidenceAt);
	if (Number.isNaN(baseline) || current > baseline) return state;
	return { curp: state.curp, status: 'running', sources: state.sources };
};
